import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  UserIcon,
  CalendarIcon,
  DocumentTextIcon,
  XMarkIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { downloadFile } from '../utils/exportUtils';

const AssetManagement = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  // New state for multi-asset form
  const [multiAssetForm, setMultiAssetForm] = useState([{
    assetName: 'Laptop',
    typeCode: 'LT',
    assetTag: '',
    assignedTo: '',
    status: 'In Use',
    purchaseDate: '',
    serialNumber: '',
    model: 'Lenovo ThinkPad E16 G3',
    location: '',
    notes: '',
    invoice: ''
  }]);

  const [formData, setFormData] = useState({
    assetName: 'Laptop',
    typeCode: 'LT',
    assetTag: '',
    assignedTo: '',
    status: 'In Use',
    purchaseDate: '',
    serialNumber: '',
    model: 'Lenovo ThinkPad E16 G3',
    location: '',
    notes: '',
    invoice: ''
  });

  const statusOptions = ['In Use', 'Retired', 'Lost', 'Damaged', 'Under Repair'];

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAssets();
      setAssets(response);
    } catch (err) {
      setError('Failed to fetch assets');
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // New function for multi-asset form input changes
  const handleMultiAssetInputChange = (index, field, value) => {
    setMultiAssetForm(prev => {
      const newForm = [...prev];
      newForm[index] = { ...newForm[index], [field]: value };
      return newForm;
    });
  };

  // New function to add a new row to the multi-asset form
  const addAssetRow = () => {
    setMultiAssetForm(prev => [...prev, {
      assetName: 'Laptop',
      typeCode: 'LT',
      assetTag: '',
      assignedTo: '',
      status: 'In Use',
      purchaseDate: '',
      serialNumber: '',
      model: 'Lenovo ThinkPad E16 G3',
      location: '',
      notes: '',
      invoice: ''
    }]);
  };

  // New function to remove a row from the multi-asset form
  const removeAssetRow = (index) => {
    if (multiAssetForm.length > 1) {
      setMultiAssetForm(prev => prev.filter((_, i) => i !== index));
    }
  };



  // New function to handle multi-asset form submission
  const handleMultiAssetSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields for all rows
      const validationErrors = [];
      multiAssetForm.forEach((asset, index) => {
        if (!asset.assetName.trim()) validationErrors.push(`Row ${index + 1}: Asset Name is required`);
        if (!asset.typeCode.trim()) validationErrors.push(`Row ${index + 1}: Type Code is required`);
        if (!asset.assetTag.trim()) validationErrors.push(`Row ${index + 1}: Asset Tag is required`);
        if (!asset.assignedTo.trim()) validationErrors.push(`Row ${index + 1}: Assigned To is required`);
        if (!asset.purchaseDate) validationErrors.push(`Row ${index + 1}: Purchase Date is required`);
        if (!asset.serialNumber.trim()) validationErrors.push(`Row ${index + 1}: Serial Number is required`);
        if (!asset.model.trim()) validationErrors.push(`Row ${index + 1}: Model is required`);
      });

      if (validationErrors.length > 0) {
        setError('Please fix the following errors:\n' + validationErrors.join('\n'));
        return;
      }

      // Filter out completely empty rows
      const validAssets = multiAssetForm.filter(asset => 
        asset.assignedTo.trim() && asset.serialNumber.trim() && asset.assetName.trim()
      );

      if (validAssets.length === 0) {
        setError('Please fill in at least one asset with required fields');
        return;
      }

      // Create all assets
      const results = [];
      for (const asset of validAssets) {
        try {
          const result = await apiService.createAsset(asset);
          results.push(result);
        } catch (err) {
          console.error('Error creating asset:', asset, err);
          // Continue with other assets even if one fails
        }
      }

      if (results.length > 0) {
        setShowForm(false);
        setMultiAssetForm([{
          assetName: 'Laptop',
          typeCode: 'LT',
          assetTag: '',
          assignedTo: '',
          status: 'In Use',
          purchaseDate: '',
          serialNumber: '',
          model: 'Lenovo ThinkPad E16 G3',
          location: '',
          notes: '',
          invoice: ''
        }]);
        fetchAssets();
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to save assets');
      console.error('Error saving assets:', err);
    }
  };

  const generateAssetTag = () => {
    const currentYear = new Date().getFullYear().toString().slice(-2);
    const randomNum = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `ALU-LT-${currentYear}-${randomNum}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAsset) {
        await apiService.updateAsset(editingAsset.id, formData);
      } else {
        await apiService.createAsset(formData);
      }
      
      setShowForm(false);
      setEditingAsset(null);
      resetForm();
      fetchAssets();
    } catch (err) {
      setError(err.message || 'Failed to save asset');
      console.error('Error saving asset:', err);
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      assetName: asset.assetName,
      typeCode: asset.typeCode,
      assetTag: asset.assetTag,
      assignedTo: asset.assignedTo,
      status: asset.status,
      purchaseDate: asset.purchaseDate.split('T')[0],
      serialNumber: asset.serialNumber,
      model: asset.model,
      location: asset.location || '',
      notes: asset.notes || '',
      invoice: asset.invoice || ''
    });
    setShowForm(true);
    // Reset multi-asset form when editing
    resetMultiAssetForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    try {
      await apiService.deleteAsset(id);
      fetchAssets();
    } catch (err) {
      setError('Failed to delete asset');
      console.error('Error deleting asset:', err);
    }
  };



  const resetForm = () => {
    setFormData({
      assetName: 'Laptop',
      typeCode: 'LT',
      assetTag: '',
      assignedTo: '',
      status: 'In Use',
      purchaseDate: '',
      serialNumber: '',
      model: 'Lenovo ThinkPad E16 G3',
      location: '',
      notes: '',
      invoice: ''
    });
  };

  // New function to reset multi-asset form
  const resetMultiAssetForm = () => {
    setMultiAssetForm([{
      assetName: 'Laptop',
      typeCode: 'LT',
      assetTag: '',
      assignedTo: '',
      status: 'In Use',
      purchaseDate: '',
      serialNumber: '',
      model: 'Lenovo ThinkPad E16 G3',
      location: '',
      notes: '',
      invoice: ''
    }]);
  };

  // New function to copy values from first row to all other rows
  const copyFirstRowToAll = () => {
    if (multiAssetForm.length > 1) {
      const firstRow = multiAssetForm[0];
      setMultiAssetForm(prev => prev.map((_, index) => 
        index === 0 ? firstRow : { ...firstRow, assetTag: '', assignedTo: '', serialNumber: '' }
      ));
    }
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.assetTag.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || asset.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      const blob = await apiService.exportAssetsToExcel();
      const filename = `Assets_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadFile(blob, filename);
    } catch (err) {
      console.error('Failed to export to Excel:', err);
      setError('Failed to export assets to Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Management</h1>
          <p className="text-gray-600">Manage laptop assets and their assignments</p>
        </div>
        <div className="flex space-x-3">
          <a
            href="https://silverbridge.sharepoint.com/:f:/s/Technology/EscRHn3ZOQ5IqzRd-irQYn0Bq8S_m3axMUsE7z2esJqEOw?e=g48G7c"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Access Invoices
          </a>
          <button
            onClick={handleExportToExcel}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingAsset(null);
              resetMultiAssetForm();
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Assets
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 whitespace-pre-line">{error}</div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, asset tag, or serial number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Asset Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-10 mx-auto p-5 border shadow-lg rounded-md bg-white ${
            editingAsset ? 'w-11/12 md:w-3/4 lg:w-1/2' : 'w-11/12 md:w-5/6 lg:w-4/5'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingAsset ? 'Edit Asset' : 'Add Multiple Assets'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingAsset(null);
                  resetMultiAssetForm();
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {editingAsset ? (
              // Single Asset Edit Form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name</label>
                    <input
                      type="text"
                      name="assetName"
                      value={formData.assetName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type Code</label>
                    <input
                      type="text"
                      name="typeCode"
                      value={formData.typeCode}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Asset Tag</label>
                    <div className="flex">
                      <input
                        type="text"
                        name="assetTag"
                        value={formData.assetTag}
                        onChange={handleInputChange}
                        className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, assetTag: generateAssetTag() }))}
                        className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
                      >
                        Generate
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                    <input
                      type="text"
                      name="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                    <input
                      type="date"
                      name="purchaseDate"
                      value={formData.purchaseDate}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                    <input
                      type="text"
                      name="serialNumber"
                      value={formData.serialNumber}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location (Optional)</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                    <input
                      type="text"
                      name="invoice"
                      value={formData.invoice}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter invoice number"
                      title="Enter invoice number or reference"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes about the asset..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingAsset(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Update Asset
                  </button>
                </div>
              </form>
            ) : (
              // Multi-Asset Form
              <>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Fill in the first row with common values (Asset Name, Type Code, Model, etc.), 
                    then use "Copy First Row to All" to apply those values to all rows. You only need to fill in the 
                    unique fields (Asset Tag, Assigned To, Serial Number) for each row. Invoice fields can contain invoice numbers or references.
                  </p>
                </div>
                <form onSubmit={handleMultiAssetSubmit} className="space-y-4">
              {/* Table Header */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Asset Name
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Type Code
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Asset Tag
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Assigned To
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Purchase Date
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Serial Number
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Model
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Location
                      </th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          Invoice Number
                        </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {multiAssetForm.map((asset, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.assetName}
                            onChange={(e) => handleMultiAssetInputChange(index, 'assetName', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.typeCode}
                            onChange={(e) => handleMultiAssetInputChange(index, 'typeCode', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.assetTag}
                            onChange={(e) => handleMultiAssetInputChange(index, 'assetTag', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.assignedTo}
                            onChange={(e) => handleMultiAssetInputChange(index, 'assignedTo', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <select
                            value={asset.status}
                            onChange={(e) => handleMultiAssetInputChange(index, 'status', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            {statusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="date"
                            value={asset.purchaseDate}
                            onChange={(e) => handleMultiAssetInputChange(index, 'purchaseDate', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.serialNumber}
                            onChange={(e) => handleMultiAssetInputChange(index, 'serialNumber', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.model}
                            onChange={(e) => handleMultiAssetInputChange(index, 'model', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.location}
                            onChange={(e) => handleMultiAssetInputChange(index, 'location', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Optional"
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={asset.invoice}
                            onChange={(e) => handleMultiAssetInputChange(index, 'invoice', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Invoice number (optional)"
                            title="Enter invoice number or reference"
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-200">
                          <button
                            type="button"
                            onClick={() => removeAssetRow(index)}
                            disabled={multiAssetForm.length === 1}
                            className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                            title="Remove row"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Row Button and Copy Values Button */}
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={addAssetRow}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Another Asset
                </button>
                {multiAssetForm.length > 1 && (
                  <button
                    type="button"
                    onClick={copyFirstRowToAll}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    Copy First Row to All
                  </button>
                )}
              </div>

              {/* Notes Field (applies to all assets) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional - applies to all assets)</label>
                <textarea
                  value={multiAssetForm[0]?.notes || ''}
                  onChange={(e) => {
                    // Apply notes to all rows
                    setMultiAssetForm(prev => prev.map(asset => ({ ...asset, notes: e.target.value })));
                  }}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional notes about the assets..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAsset(null);
                    resetMultiAssetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Add {multiAssetForm.length} Asset{multiAssetForm.length !== 1 ? 's' : ''}
                </button>
              </div>
            </form>
            </>
            )}
          </div>
        </div>
      )}

      {/* Assets Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Assets ({filteredAssets.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssets.map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <ComputerDesktopIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{asset.assetName}</div>
                        <div className="text-sm text-gray-500">{asset.assetTag}</div>
                        <div className="text-sm text-gray-500">{asset.model}</div>
                        <div className="text-sm text-gray-500">SN: {asset.serialNumber}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <div className="text-sm text-gray-900">{asset.assignedTo}</div>
                    </div>
                    {asset.location && (
                      <div className="text-sm text-gray-500 mt-1">{asset.location}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <div className="text-sm text-gray-900">{formatDate(asset.purchaseDate)}</div>
                    </div>
                    {asset.invoice && (
                      <div className="flex items-center mt-1">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <div className="text-sm text-gray-500">{asset.invoice}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      asset.status === 'In Use' ? 'bg-green-100 text-green-800' :
                      asset.status === 'Retired' ? 'bg-gray-100 text-gray-800' :
                      asset.status === 'Lost' ? 'bg-red-100 text-red-800' :
                      asset.status === 'Damaged' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {asset.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(asset)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(asset.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredAssets.length === 0 && (
          <div className="text-center py-12">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No assets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter ? 'Try adjusting your search or filter criteria.' : 'Get started by adding a new asset.'}
            </p>
          </div>
        )}
      </div>


    </div>
  );
};

export default AssetManagement;
